import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Grid, Tab, Tabs, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import {
  CreateOrderRequestDto,
  CreateOrderRequestFormDto,
  TradeOrderSideEnum,
  TradeOrderTypeEnum,
  IInstrument,
  normalizeQuantity,
  normalizeAmount,
  normalizePrice,
  validateAmount,
  validatePrice,
  validateQuantity,
  ValidationConstraints,
} from '@sparling/financial-common'
import { useDispatch, useSelector } from 'react-redux'
import Decimal from 'decimal.js'
import { get, isNil } from 'lodash'
import { InputSelect } from '../../../../../components/Dashboard/InputSelect'
import { GreenButton } from '../../../../../components/Dashboard/Buttons/GreenButton'
import { ORDER_SIDE } from '../../../../../constants/orderSideList'
import {
  currentNameInstrumentSelector,
  currentPriceSelector,
  getUserCommissionFee,
  instrumentsOptionsSelector,
  instrumentsSelector,
  makeQuoteCurrencyBalanceSelector,
} from '../../../../../store/selectors'
import { RootState } from '../../../../../store/types'
import { TradingInput } from './TradingInput'
import { changeOrderSide, createOrderRequest, currentInstrument } from '../../../../../store/actions'
import { orderSideSelector } from '../../../../../store/selectors/app'
import { useCommissionPreview } from '../../../../../hooks/useCommissionPreview'
import { formatDisplayCurrency } from '../../../../../utils/formatDisplayCurrency'
import { TradingFormConfirmationModal } from './TradingFormConfirmationModal'
import { GridRow } from '../../../../../components/GridRow'
import { useBalanceValidator } from '../../../../../hooks/useBalanceValidator'
import { useFocusField } from '../../../../../hooks/useFocusField'

const useStyles = makeStyles((theme: Theme) => ({
  wrap: {
    display: 'flex',
    width: '100%',
    height: '100%',
    flexDirection: 'column',
    paddingTop: theme.spacing(4),
  },
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: 40,
    maxHeight: 40,
    paddingBottom: theme.spacing(2),
  },
  section: {
    justifyContent: 'space-between',
    padding: theme.spacing(1, 3, 1, 3),
    // background: 'rgb(49,52,58)',
    background: theme.palette.background.paper,
  },
  tabs: {
    justifyContent: 'space-between',
    borderBottom: '1px solid rgba(233,236,239, 0.38)',
    '& .MuiTabs-indicator': {
      backgroundColor: theme.palette.secondary.main,
    },
    '& button': {
      textTransform: 'none',
      minWidth: 50,
      color: theme.palette.primary.light,
      '&.Mui-selected': {
        color: theme.palette.secondary.main,
      },
    },
  },
  infoField: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.primary.light,
  },
}))

interface FormValues extends CreateOrderRequestDto {
  amount?: number
}

const tabs = [{ tabLabel: 'RFQ' }, { tabLabel: 'Limit' }]

export const TradingForm: FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const currentAsset = useSelector(currentNameInstrumentSelector)
  const instruments = useSelector(instrumentsSelector)
  const instrumentOptions = useSelector(instrumentsOptionsSelector)
  const currentPrice = useSelector(currentPriceSelector)
  const orderSide = useSelector(orderSideSelector)
  const commissionFee = useSelector(getUserCommissionFee)
  const [tab, setTab] = useState<number>(0)
  const [lastEditedField, setLastEditedField] = useState<'quantity' | 'amount'>('quantity')
  const [showModal, setShowModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { focusedField, registerFocus } = useFocusField<'quantity' | 'amount'>()

  const selectedInstrument = useMemo(
    () => instruments.find((item) => item.name === currentAsset),
    [currentAsset, instruments],
  )

  const quantityPrecision = useMemo(() => {
    if (!selectedInstrument) return undefined
    return selectedInstrument.quantityPrecision ?? 0
  }, [selectedInstrument])

  const priceSignificantDigits = useMemo(() => {
    if (!selectedInstrument) return undefined
    return selectedInstrument.priceSignificantDigits ?? 0
  }, [selectedInstrument])

  const [baseCurrency, quoteCurrency] = useMemo(() => {
    if (!selectedInstrument?.underlier || selectedInstrument.underlier.length !== 6) {
      return ['', '']
    }

    return [selectedInstrument.underlier.slice(0, 3), selectedInstrument.underlier.slice(3)]
  }, [selectedInstrument])

  const quoteCurrencyBalanceSelector = useMemo(makeQuoteCurrencyBalanceSelector, [])
  const quoteCurrencyBalance = useSelector((state: RootState) => quoteCurrencyBalanceSelector(state, quoteCurrency))

  const maxAmountWithCommission = useMemo(() => {
    if (quoteCurrencyBalance > 0 && commissionFee && commissionFee > 0) {
      return quoteCurrencyBalance / (1 + commissionFee)
    }
    return quoteCurrencyBalance
  }, [quoteCurrencyBalance, commissionFee])

  const formMethods = useForm<FormValues>({
    mode: 'all',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver: classValidatorResolver(CreateOrderRequestFormDto),
    defaultValues: {
      orderType: TradeOrderTypeEnum.MKT,
      side: orderSide,
      quantity: selectedInstrument?.minQuantityPerTrade,
      selectedInstrument,
      ...(baseCurrency && { currency: baseCurrency }),
      ...(currentAsset && { instrument: currentAsset }),
      ...(currentPrice && { price: currentPrice }),
      ...(currentPrice && { currentPrice }),
    },
    context: { selectedInstrument, currentPrice },
  })

  const {
    clearErrors,
    handleSubmit,
    formState: { isValid },
    reset,
    resetField,
    watch,
    setValue,
    getValues,
    control,
  } = formMethods

  const formValues = useWatch({
    control,
  })

  const handleTabChange = useCallback(
    (_: React.ChangeEvent<unknown>, newValue: number) => {
      const newOrderType = newValue === 0 ? TradeOrderTypeEnum.MKT : TradeOrderTypeEnum.FOK
      setTab(newValue)
      setValue('orderType', newOrderType)
      if (currentPrice && newOrderType === TradeOrderTypeEnum.MKT) {
        setValue('price', currentPrice)
        clearErrors('price')
      } else {
        setValue('price', 0)
      }
      return newValue
    },
    [setValue, currentPrice, clearErrors],
  )

  const setInitialValues = useCallback(() => {
    const quantity = getValues('quantity')
    if (quantity) {
      setValue('amount', new Decimal(quantity).mul(currentPrice).toNumber())
    }
  }, [currentPrice, getValues, setValue])

  const commissionData = useCommissionPreview({
    instrument: selectedInstrument as IInstrument,
    side: orderSide,
    quantity: Number(getValues('quantity')) || selectedInstrument?.minQuantityPerTrade || 0,
    commissionRate: commissionFee as number,
  })

  const balanceValidation = useBalanceValidator({
    side: orderSide,
    baseCurrency,
    quoteCurrency,
    amount: formValues.amount || 0,
    quantity: formValues.quantity || 0,
    commissionAmount: commissionData?.commissionAmount || 0,
    isCommissionInQuote: commissionData?.isCommissionInQuote || false,
  })

  const displayPriceWithCommission = useMemo(() => {
    const rawPrice = formValues?.price
    const side = (formValues?.side as TradeOrderSideEnum) || orderSide
    if (!rawPrice || !commissionFee || !commissionData?.isCommissionInQuote) return rawPrice
    return side === TradeOrderSideEnum.BUY ? rawPrice * (1 + commissionFee) : rawPrice * (1 - commissionFee)
  }, [formValues?.price, formValues?.side, commissionFee, commissionData, orderSide])

  const handleConfirmTrade = useCallback(
    (data: FormValues) => {
      const { amount, quantity, price, ...rest } = data

      let normalizedQty = quantity
      let normalizedAmount = amount
      let normalizedPrice: number | undefined = price

      if (selectedInstrument && quantityPrecision !== undefined) {
        const qtyConstraints: ValidationConstraints = {
          min: selectedInstrument.minQuantityPerTrade,
          max: selectedInstrument.maxQuantityPerTrade,
          precision: quantityPrecision,
        }
        normalizedQty = normalizeQuantity(quantity ?? 0, qtyConstraints).toNumber()

        const amountConstraints: ValidationConstraints = {
          min: 0,
          max: maxAmountWithCommission,
          precision: quantityPrecision,
        }
        normalizedAmount = normalizeAmount(amount ?? 0, amountConstraints).toNumber()

        if (priceSignificantDigits && price !== undefined) {
          normalizedPrice = normalizePrice(price, priceSignificantDigits).toNumber()
        }
      }

      dispatch(
        createOrderRequest({
          ...rest,
          price: normalizedPrice,
          ...(lastEditedField === 'quantity' ? { quantity: normalizedQty } : { amount: normalizedAmount }),
        }),
      )

      if (selectedInstrument && currentAsset && currentPrice) {
        reset({
          quantity: selectedInstrument.minQuantityPerTrade,
          instrument: currentAsset,
          selectedInstrument,
          price: normalizedPrice ?? data.price,
        })
      }
    },
    [
      dispatch,
      reset,
      selectedInstrument,
      currentAsset,
      currentPrice,
      lastEditedField,
      quantityPrecision,
      priceSignificantDigits,
      maxAmountWithCommission,
    ],
  )

  const handleModalClose = useCallback(() => {
    setShowModal(false)
    setLastEditedField('quantity')
    setInitialValues()
  }, [setInitialValues])

  const handleModalConfirm = () => {
    if (isSubmitting) return

    setIsSubmitting(true)
    handleConfirmTrade(formValues as FormValues)
    setIsSubmitting(false)
    handleModalClose()
  }

  const onSubmit = useCallback((data: FormValues) => {
    if (data && data.quantity && data.price) {
      setShowModal(true)
    }
  }, [])

  useEffect(() => {
    setInitialValues()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (type !== 'change' || !name) return

      const side = getValues('side') as TradeOrderSideEnum
      const commission = new Decimal(commissionData?.commissionInQuote || 0)
      const priceRaw = getValues('price')
      const quantityRaw = getValues('quantity')
      const amountRaw = getValues('amount')
      const quantityFieldIsFocused = focusedField === 'quantity'
      const amountFieldIsFocused = focusedField === 'amount'

      const price = new Decimal(priceRaw || 0)
      const quantity = new Decimal(quantityRaw || 0)
      const amount = new Decimal(amountRaw || 0)

      let normalizedQuantity = quantity
      let normalizedAmount = amount
      let normalizedPrice = price

      if (selectedInstrument && quantityPrecision !== undefined) {
        const qtyConstraints: ValidationConstraints = {
          min: selectedInstrument.minQuantityPerTrade,
          max: selectedInstrument.maxQuantityPerTrade,
          precision: quantityPrecision,
        }
        normalizedQuantity = normalizeQuantity(quantity, qtyConstraints)

        const amountConstraints: ValidationConstraints = {
          min: 0,
          max: Number.MAX_SAFE_INTEGER,
          precision: quantityPrecision,
        }
        normalizedAmount = normalizeAmount(amount, amountConstraints)

        if (priceSignificantDigits) {
          normalizedPrice = normalizePrice(price, priceSignificantDigits)
        }
      }

      if (name === 'quantity' && !normalizedQuantity.eq(quantity) && !quantityFieldIsFocused) {
        setValue('quantity', normalizedQuantity.toNumber(), { shouldValidate: true })
      }

      if (name === 'amount' && !normalizedAmount.eq(amount) && !amountFieldIsFocused) {
        setValue('amount', normalizedAmount.toNumber(), { shouldValidate: true })
      }

      if (name === 'price' && !normalizedPrice.eq(price)) {
        setValue('price', normalizedPrice.toNumber(), { shouldValidate: true })
      }

      if (!normalizedPrice.isZero()) {
        if (name === 'quantity' && lastEditedField === 'quantity' && !amountFieldIsFocused) {
          const gross = normalizedQuantity.mul(normalizedPrice)
          const amountWithCommission =
            side === TradeOrderSideEnum.BUY ? gross.plus(commission) : gross.minus(commission)

          const amountConstraints: ValidationConstraints = {
            min: 0,
            max: Number.MAX_SAFE_INTEGER,
            precision: quantityPrecision ?? 0,
          }

          const finalAmount = normalizeAmount(amountWithCommission, amountConstraints)
          setValue('amount', finalAmount.toNumber(), { shouldValidate: true })
        } else if (name === 'amount' && lastEditedField === 'amount' && !quantityFieldIsFocused) {
          const grossFromAmount =
            side === TradeOrderSideEnum.BUY ? normalizedAmount.minus(commission) : normalizedAmount.plus(commission)

          const calculatedQuantity = grossFromAmount.div(normalizedPrice)

          const qtyConstraints: ValidationConstraints = {
            min: selectedInstrument?.minQuantityPerTrade ?? 0,
            max: selectedInstrument?.maxQuantityPerTrade ?? Number.MAX_SAFE_INTEGER,
            precision: quantityPrecision ?? 0,
          }

          const finalQuantity = normalizeQuantity(calculatedQuantity, qtyConstraints)
          setValue('quantity', finalQuantity.toNumber(), { shouldValidate: true })
        } else if (name === 'price') {
          if (lastEditedField === 'quantity' && !amountFieldIsFocused) {
            const gross = normalizedQuantity.mul(normalizedPrice)
            const amountWithCommission =
              side === TradeOrderSideEnum.BUY ? gross.plus(commission) : gross.minus(commission)

            const amountConstraints: ValidationConstraints = {
              min: 0,
              max: Number.MAX_SAFE_INTEGER,
              precision: quantityPrecision ?? 0,
            }

            const finalAmount = normalizeAmount(amountWithCommission, amountConstraints)
            setValue('amount', finalAmount.toNumber(), { shouldValidate: true })
          } else {
            const grossFromAmount =
              side === TradeOrderSideEnum.BUY ? normalizedAmount.minus(commission) : normalizedAmount.plus(commission)

            const calculatedQuantity = grossFromAmount.div(normalizedPrice)

            const qtyConstraints: ValidationConstraints = {
              min: selectedInstrument?.minQuantityPerTrade ?? 0,
              max: selectedInstrument?.maxQuantityPerTrade ?? Number.MAX_SAFE_INTEGER,
              precision: quantityPrecision ?? 0,
            }

            const finalQuantity = normalizeQuantity(calculatedQuantity, qtyConstraints)
            // eslint-disable-next-line no-unused-expressions
            !quantityFieldIsFocused && setValue('quantity', finalQuantity.toNumber(), { shouldValidate: true })
          }
        }
      }

      if (name === 'side') {
        dispatch(changeOrderSide(value[name] as TradeOrderSideEnum))
        resetField('quantity')
      }

      if (name === 'instrument' && value?.instrument) {
        dispatch(currentInstrument(value.instrument))
        resetField('quantity')
      }
    })
    return () => subscription.unsubscribe()
  }, [
    dispatch,
    watch,
    getValues,
    setValue,
    lastEditedField,
    commissionData,
    selectedInstrument,
    quantityPrecision,
    priceSignificantDigits,
    maxAmountWithCommission,
    focusedField,
    resetField,
  ])

  useEffect(() => {
    const isExist = instrumentOptions.find((item) => item.value === currentAsset)
    if (instrumentOptions.length && currentAsset && !isExist) {
      dispatch(currentInstrument(null))
      reset()
      setValue('instrument', '')
    }
  }, [dispatch, currentAsset, reset, setValue, instrumentOptions])

  useEffect(() => {
    if (selectedInstrument) {
      setValue('selectedInstrument', selectedInstrument)
      setValue('currency', baseCurrency)

      const { quantity, amount } = getValues()
      const { minQuantityPerTrade, maxQuantityPerTrade } = selectedInstrument

      if (
        !validateQuantity(quantity || 0, {
          min: minQuantityPerTrade,
          max: maxQuantityPerTrade,
          precision: quantityPrecision || 0,
        }).isValid &&
        !['amount', 'quantity'].includes(focusedField || '')
      ) {
        setValue('quantity', minQuantityPerTrade)
      }

      if (
        !validateAmount(amount || 0, {
          min: new Decimal(minQuantityPerTrade).mul(currentPrice),
          max: new Decimal(maxQuantityPerTrade).mul(currentPrice),
          precision: quantityPrecision || 0,
        }).isValid &&
        !['amount', 'quantity'].includes(focusedField || '')
      ) {
        setInitialValues()
      }
    }

    if (currentPrice) {
      setValue('currentPrice', currentPrice)
      if (getValues().orderType === TradeOrderTypeEnum.MKT) {
        setValue('price', currentPrice)
      }
    }
    if (currentAsset) setValue('instrument', currentAsset)
  }, [
    selectedInstrument,
    currentPrice,
    getValues,
    setValue,
    currentAsset,
    baseCurrency,
    focusedField,
    setInitialValues,
    quantityPrecision,
    maxAmountWithCommission,
  ])

  return (
    <Grid item xs={12} className={classes.wrap}>
      <Grid item xs={12} className={classes.container}>
        <Typography variant={'h6'}>Trade</Typography>
      </Grid>

      <Grid container item xs={12} className={classes.section}>
        <Grid container className={classes.tabs}>
          <Tabs value={tab} onChange={handleTabChange}>
            {tabs.map((current, i) => (
              <Tab key={i} label={current.tabLabel} />
            ))}
          </Tabs>
        </Grid>
      </Grid>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <input type="hidden" {...formMethods.register('currency')} />
          <input type="hidden" {...formMethods.register('orderType')} />
          <input type="hidden" {...formMethods.register('selectedInstrument')} />
          <Grid container className={classes.section}>
            <GridRow spacing={4} display={'flex'} direction={'row'}>
              <Grid item xs={4}>
                <InputSelect
                  inputName={'side'}
                  width={'100%'}
                  enums={ORDER_SIDE}
                  mb={16}
                  mt={16}
                  defaultValue={orderSide}
                />
              </Grid>
              <Grid item xs={8}>
                <InputSelect
                  inputName={'instrument'}
                  width={'100%'}
                  enums={instrumentOptions}
                  pl={16}
                  mb={16}
                  mt={16}
                />
              </Grid>
            </GridRow>
            <GridRow spacing={2}>
              <Grid item xs={6}>
                <TradingInput
                  inputName="quantity"
                  labelName="Quantity"
                  postfix={baseCurrency}
                  thousandSeparator
                  decimalScale={5}
                  placeholder="0.00000"
                  onClick={() => setLastEditedField('quantity')}
                  {...registerFocus('quantity')}
                />
              </Grid>

              <Grid item xs={6}>
                <TradingInput
                  inputName="amount"
                  labelName="Amount"
                  postfix={quoteCurrency}
                  thousandSeparator
                  decimalScale={2}
                  placeholder="0.00"
                  onClick={() => setLastEditedField('amount')}
                  {...registerFocus('amount')}
                />
              </Grid>
            </GridRow>
            <Grid item xs={12}>
              <TradingInput
                inputName="price"
                labelName="Price"
                disable={getValues().orderType === TradeOrderTypeEnum.MKT}
                postfix={quoteCurrency}
                customValue={displayPriceWithCommission}
                decimalScale={2}
                fixedDecimalScale
                rules={{
                  validate: (value: string) => {
                    if (getValues().orderType === TradeOrderTypeEnum.MKT) return true
                    if (!priceSignificantDigits) return true

                    const result = validatePrice(value, priceSignificantDigits)
                    return result.isValid || result.error?.message || 'Invalid price'
                  },
                }}
                onValueTransform={(val) => {
                  if (val === undefined || val === null) return val
                  if (!commissionFee || !commissionData?.isCommissionInQuote) {
                    if (priceSignificantDigits) {
                      return normalizePrice(val, priceSignificantDigits).toNumber()
                    }
                    return val
                  }

                  const side = (getValues('side') as TradeOrderSideEnum) || orderSide
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  const raw = side === TradeOrderSideEnum.BUY ? val / (1 + commissionFee) : val / (1 - commissionFee)
                  return priceSignificantDigits ? normalizePrice(raw, priceSignificantDigits).toNumber() : raw
                }}
              />
            </Grid>
            {!isNil(commissionData?.commissionAmount) && get(commissionData, 'commissionAmount', 0) >= 0.01 && (
              <GridRow spacing={2}>
                <Grid item xs={6}>
                  <Typography className={classes.infoField}>Commission ({commissionFee}):</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography className={classes.infoField}>
                    {formatDisplayCurrency(
                      get(commissionData, 'commissionAmount'),
                      get(commissionData, 'commissionCurrency'),
                    )}
                  </Typography>
                </Grid>
              </GridRow>
            )}
            <Grid item xs={12}>
              <GreenButton
                text={'Trade'}
                width={'100%'}
                mt={16}
                mb={16}
                type={'button'}
                disabled={!(isValid && !!currentPrice && balanceValidation.isEnoughBalance)}
                onClick={handleSubmit(onSubmit)}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant={'subtitle2'} color={'#e6e6e6'} textAlign={'center'}>
                Please enter a quantity or amount to see a quote
              </Typography>
            </Grid>
          </Grid>
        </form>
      </FormProvider>

      <TradingFormConfirmationModal
        open={showModal}
        data={formValues as FormValues}
        onConfirm={handleModalConfirm}
        onCancel={handleModalClose}
      />
    </Grid>
  )
}
